import React, { useEffect, useMemo, useState, Fragment } from "react"
import useStyles from "./styles"
import { Typography, Box, TextField } from "@material-ui/core"
import AutocompleteBilling from "../../Common/AutocompleteBilling/index"
import { useDispatch, useSelector } from "react-redux"
import { getClientCompanies, getClientList } from "../../../../actions/clients"
import { MuiThemeProvider } from "@material-ui/core/styles"
import PropTypes from "prop-types"
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers"
import { pickersTheme } from "../../../../themes/billing"
import DateFnsUtils from "@date-io/date-fns"
import moment from "moment"
import SelectOrganizationHeader from "../../Common/SelectOrganizationHeader"
import useActiveCompanyAdesk from "../../../../hooks/useActiveCompanyAdesk"
import { updateSolo } from "../../../../actions/solos"
import { openSnackBar } from "../../../../actions/snack-bar"
import AdditionalBillingData from "../../Admin/AdditionalBillingData/index"

const SoloTableHeader = ({
  type = "",
  soloData = {},
  setSoloData = () => {},
  adBillingValue = "",
  setAdBillingValue = () => {},
  setClient = () => {},
  client = null,
}) => {
  const [selectWorking, toggleSelectWorking] = useState(false)
  const classes = useStyles()
  const dispatch = useDispatch()
  const isAdesk = useActiveCompanyAdesk()

  const soloDate = soloData && soloData[`${type.toLowerCase()}_shoot_date`]

  const clientData = useSelector(state => state.clients.clients)
  const companyData = useSelector(state => state.clients.companiesList)

  const id = soloData && soloData[`${type.toLowerCase()}_id`]

  const setNewDate = e => {
    let newDate = moment(e).format()
    setSoloData(oldData => ({
      ...oldData,
      [`${type.toLowerCase()}_shoot_date`]: newDate,
    }))
  }

  useEffect(
    () => {
      setAdBillingValue(soloData.ad_billing_note)
    },
    [soloData.ad_billing_note]
  )

  useEffect(() => {
    if (clientData.length === 0) {
      dispatch(getClientList())
    }
  }, [])

  useEffect(() => {
    if (companyData.length === 0) {
      dispatch(getClientCompanies())
    }
  }, [])

  const isDisabled = useMemo(
    () => {
      // if solo invoice, disable if full paid; if solo estimate disable if linked to shoot
      return type === "invoice"
        ? soloData && soloData.full_paid
        : soloData && (soloData.shoot_id || soloData.shoot_name)
    },
    [soloData]
  )

  const handleChangeCompanySolo = e => {
    toggleSelectWorking(true)
    let newData = { crewmama_or_gtt: e.target.value }
    dispatch(updateSolo(type, id, newData))
      .then(() => {
        setSoloData({ ...soloData, crewmama_or_gtt: e.target.value })
        dispatch(openSnackBar({ message: "Updated sender organization" }))
      })
      .catch(err => {
        console.log(err)
        dispatch(openSnackBar({ message: "Failed to change organization" }))
      })
      .finally(() => {
        toggleSelectWorking(false)
      })
  }

  // Additional Billing Data

  const handelAdBillingChange = e => {
    setAdBillingValue(e.target.value)
  }

  return (
    <>
      <Box className={classes.container}>
        <Box className={classes.leftContainer}>
          <Typography
            style={{ color: "#9A9A9A", fontWeight: 600, size: "14px" }}
          >
            {type && type.toUpperCase()} FOR
          </Typography>
          <Box>
            {clientData.length > 0 && (
              <AutocompleteBilling
                options={clientData}
                getOptionLabel={option =>
                  option.first_name + " " + option.last_name
                }
                placeholder={"Client Name"}
                value={clientData.filter(x => x.id === client.client_id)}
                className={classes.autocomplete}
                blurInputOnSelect
                backspaceRemovesValue
                showValue
                caps
                disabled={isDisabled}
                onChange={value => {
                  setClient({
                    client_id: value.id,
                    client_name: value.first_name + " " + value.last_name,
                    client_email: value.email,
                    client_phone: value.phone,
                  })
                }}
              />
            )}
          </Box>
          <Box style={{ marginTop: "10px" }}>
            {companyData.length > 0 && (
              <AutocompleteBilling
                placeholder={"Company Name"}
                options={companyData}
                getOptionLabel={option => option.name}
                labelKey="name"
                value={companyData.filter(
                  x => x.id === soloData.client_company_id
                )}
                className={classes.autocomplete}
                blurInputOnSelect
                backspaceRemovesValue
                showValue
                caps
                disabled={isDisabled}
                onChange={value => {
                  setSoloData(oldState => ({
                    ...oldState,
                    client_company_id: value.id,
                    client_company_name: value.name,
                    client_company_location: value.location,
                    client_company_logo_image: value.logo_image,
                    client_check_payment: value.check_payment,
                  }))
                }}
              />
            )}
          </Box>
          <Box style={{ marginTop: "10px" }}>
            <AdditionalBillingData
              onChange={handelAdBillingChange}
              adBillingValue={adBillingValue}
              loading={false}
              saveButton={false}
              disabled={isDisabled}
              width="100%"
            />
          </Box>
        </Box>
        <Box className={classes.middleContainer}>
          <Typography
            style={{ color: "#9A9A9A", fontWeight: 600, size: "14px" }}
          >
            {type && type.toUpperCase()} FROM
          </Typography>
          {isAdesk ? (
            <SelectOrganizationHeader
              headerInfo={soloData}
              changeCompanyFn={handleChangeCompanySolo}
              working={selectWorking}
            />
          ) : (
            <Typography>{soloData.company_name}</Typography>
          )}
        </Box>
        <Box className={classes.rightContainer}>
          <Box className={classes.idContainer}>
            <Typography
              style={{ color: "#9A9A9A", fontWeight: 600, size: "14px" }}
            >
              {type && type.toUpperCase()} ID
            </Typography>
            <TextField
              value={id || ""}
              fullWidth
              type="text"
              disabled
              variant="outlined"
              className={classes.textfield}
              inputProps={{
                style: { padding: "10px" },
              }}
            />
          </Box>
          <Box className={classes.dateContainer}>
            <Fragment>
              <Typography
                style={{ color: "#9A9A9A", fontWeight: 600, size: "14px" }}
              >
                {type && type.toUpperCase()} DATE
              </Typography>
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <MuiThemeProvider theme={pickersTheme}>
                  <KeyboardDatePicker
                    autoOk
                    value={soloDate}
                    onChange={e => setNewDate(e)}
                    variant="inline"
                    inputVariant="outlined"
                    format="MM/dd/yyyy"
                    disabled={isDisabled}
                    InputProps={{
                      fullWidth: true,
                      inputProps: {
                        style: {
                          padding: "10px",
                          paddingRight: 0,
                        },
                      },
                      classes: {
                        adornedEnd: classes.pickerInput,
                      },
                    }}
                    InputAdornmentProps={{
                      style: { marginLeft: "0" },
                    }}
                    KeyboardButtonProps={{
                      "aria-label": "change-invoice-date",
                      style: { padding: "6px" },
                    }}
                  />
                </MuiThemeProvider>
              </MuiPickersUtilsProvider>
            </Fragment>
          </Box>
        </Box>
      </Box>
    </>
  )
}

SoloTableHeader.propTypes = {
  type: PropTypes.string,
  soloData: PropTypes.object,
  setSoloData: PropTypes.func,
  client: PropTypes.number,
  setClient: PropTypes.func,
}

export default SoloTableHeader
